import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { API_BASE_URL, ACCESSDB_API_BASE_URL } from '../config/constants';
import { getErrorMessageByCode } from '../config/errorMessages';
import { useAuthStore } from '../stores/authStore';
import { showToast } from '../stores/uiStore';
import type { ToastOptions } from '../stores/uiStore';
import type { ApiResponse, ApiError } from '../types';
import { ROUTES } from '../config/routes';
import { navigateTo } from './navigation';
import { evaluateGuard, authGate, cbRegistry, markAuthStopped, guardBroadcaster } from '../lib/apiGuard';
import { guardedNavigate } from '../lib/apiGuard/guardedNavigation';

const normalizeEndpointKey = (url?: string): string => {
  if (!url) return 'unknown';
  return url.split('?')[0];
};

// JWT exp デコード helper — proactive/定期refresh用
export const decodeJwtExp = (idToken: string): number => {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    const exp = payload?.exp;
    if (typeof exp !== 'number') return 0;
    return Math.max(0, exp * 1000 - Date.now());  // ms残時間
  } catch {
    return 0;
  }
};

declare module 'axios' {
  interface AxiosRequestConfig {
    skipGlobalErrorToast?: boolean;
  }
}

// グローバル refresh mutex: 並行401で1本のみrefreshを実行
let refreshPromise: Promise<string> | null = null;

// 内部refresh関数 (export seam — テスト可能な継ぎ目)
// ★critical-2: 生axios.postのみ使用。api/accessDBApi経由禁止
export const _doRefresh = async (): Promise<string> => {
  const stored = JSON.parse(localStorage.getItem('entrybot_auth') || '{}');
  const refreshToken = stored?.state?.refreshToken ?? useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error('no_refresh_token');
  const response = await axios.post<ApiResponse<{ tokens: { access_token: string; id_token: string; expires_in: number } }>>(
    `${API_BASE_URL}/auth/refresh`,
    { refresh_token: refreshToken }
  );
  const tokens = response.data?.data?.tokens;
  if (response.data.success && tokens?.id_token && tokens?.access_token) {
    useAuthStore.getState().setTokens({
      access_token: tokens.access_token,
      id_token: tokens.id_token,
      refresh_token: refreshToken,
      expires_in: decodeJwtExp(tokens.id_token),
    });
    return tokens.id_token;
  }
  throw new Error('refresh_failed');
};

// 共有失敗ポリシー: 401/403→setSessionExpired(true) / network/5xx→セッション維持
export const handleRefreshFailure = (err: unknown): void => {
  const status = (err as AxiosError)?.response?.status;
  if (status === 401 || status === 403) {
    useAuthStore.getState().setSessionExpired(true);
  }
  // network/timeout/5xx: 何もしない（セッション維持）
};

// mutex + finally抽出 (export seam — テスト可能な継ぎ目)
export const _refreshWithMutex = async (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = _doRefresh();
  }
  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

// 401エラー共通ハンドラ
const handle401Error = async (
  error: AxiosError<ApiResponse<unknown>>,
  axiosInstance: AxiosInstance,
  getToken: () => string,
): Promise<AxiosResponse> => {
  const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

  if (originalRequest && !originalRequest._retry) {
    originalRequest._retry = true;

    try {
      await _refreshWithMutex();
      authGate.reset();
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${getToken()}`;
      }
      return axiosInstance(originalRequest);
    } catch (err: unknown) {
      authGate.markStopped();
      markAuthStopped();           // persist to localStorage → other tabs' storage event
      guardBroadcaster.publish('auth_stopped');  // BC fast path → other tabs
      handleRefreshFailure(err);
    }
  }

  return Promise.reject(error);
};

const getIdTokenWithFallback = (): string | null => {
  const storeToken = useAuthStore.getState().idToken;
  if (storeToken) return storeToken;
  try {
    const raw = localStorage.getItem('entrybot_auth');
    if (raw) {
      const parsed = JSON.parse(raw);
      return parsed?.state?.idToken ?? null;
    }
  } catch {
    // parse失敗時はnullを返す
  }
  return null;
};

// エラー種別ごとのアクションボタン付きトーストオプションを返す
const getActionToastOptions = (
  error: unknown,
  axiosInstance: AxiosInstance,
): { message: string; options: ToastOptions } | null => {
  if (!axios.isAxiosError(error)) return null;
  const axiosError = error as AxiosError<ApiResponse<unknown>>;
  const status = axiosError.response?.status;
  const url = axiosError.config?.url ?? '';
  const responseData = axiosError.response?.data as any;
  const errorCode = responseData?.error?.code ?? '';

  // KB sync error（URLまたはerror_codeに"sync"を含む場合）
  if ((typeof errorCode === 'string' && errorCode.includes('sync')) || url.includes('sync')) {
    const originalConfig = axiosError.config;
    return {
      message: '同期に失敗しました',
      options: {
        action: {
          label: '再同期',
          onClick: () => { if (originalConfig) axiosInstance(originalConfig); },
        },
      },
    };
  }

  // 429 Rate Limit
  if (status === 429) {
    const retryAfter = responseData?.retry_after;
    const originalConfig = axiosError.config;
    return {
      message: 'リクエスト制限中です',
      options: {
        action: {
          label: retryAfter ? `${retryAfter}秒後に再試行` : '再試行',
          onClick: () => {
            const delay = retryAfter ? retryAfter * 1000 : 0;
            setTimeout(() => { if (originalConfig) axiosInstance(originalConfig); }, delay);
          },
        },
      },
    };
  }

  // Network Error（レスポンスなし）
  if (!axiosError.response && axiosError.message === 'Network Error') {
    const originalConfig = axiosError.config;
    return {
      message: '通信に失敗しました',
      options: {
        action: {
          label: '再試行',
          onClick: () => { if (originalConfig) axiosInstance(originalConfig); },
        },
      },
    };
  }

  // 500 Server Error
  if (status === 500) {
    return {
      message: 'サーバーエラーが発生しました',
      options: {
        action: {
          label: 'サポートに問い合わせ',
          onClick: () => { guardedNavigate('/contact'); },
        },
      },
    };
  }

  // 403 Forbidden
  if (status === 403) {
    const beErrorCode = responseData?.error?.code;
    if (beErrorCode === 'PLAN_001') {
      if (!axiosError.config?.skipGlobalErrorToast && window.location.pathname !== ROUTES.SUBSCRIPTION) {
        navigateTo(ROUTES.SUBSCRIPTION);
      }
      return null;
    }
    if (beErrorCode === 'FEATURE_PRO_ONLY') {
      return null;  // 汎用トースト非発火・呼出側catchへ素通し
    }
    return {
      message: 'この操作を行う権限がありません',
      options: {},
    };
  }

  return null;
};

// Axios インスタンス作成
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエストインターセプター
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const key = normalizeEndpointKey(config.url);
    const guard = evaluateGuard({ endpointKey: key });
    if (!guard.allowed) {
      return Promise.reject(new axios.Cancel(`Guard blocked: ${guard.reason}`));
    }
    const { isSessionExpired } = useAuthStore.getState();
    const idToken = getIdTokenWithFallback();
    if (isSessionExpired) {
      return Promise.reject(new axios.Cancel('Session expired'));
    }
    if (idToken && config.headers) {
      config.headers.Authorization = `Bearer ${idToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
api.interceptors.response.use(
  (response: AxiosResponse) => {
    cbRegistry.recordSuccess(normalizeEndpointKey(response.config?.url));
    return response;
  },
  async (rawError: unknown) => {
    if (axios.isCancel(rawError)) {
      return Promise.reject(rawError);
    }
    const error = rawError as AxiosError<ApiResponse<unknown>>;
    const status = error.response?.status;
    const key = normalizeEndpointKey(error.config?.url);
    const responseData = error.response?.data as any;
    const code = responseData?.error?.code ?? '';
    if (status === 401) {
      authGate.on401or403(code);
      if (code === 'USER_NOT_FOUND') {
        useAuthStore.getState().clearAuth();
        useAuthStore.getState().setSessionExpired(true);
        return Promise.reject(error);
      }
      return handle401Error(error, api, () => useAuthStore.getState().idToken ?? '');
    }
    if (status === 403 && code !== 'PLAN_001') {
      authGate.on401or403(code);
    }
    if ((status && status >= 500) || (!status && !error.response)) {
      cbRegistry.recordFailure(key);
    }
    const actionToast = getActionToastOptions(error, api);
    if (actionToast && !error.config?.skipGlobalErrorToast) {
      showToast.error(actionToast.message, actionToast.options);
    }
    return Promise.reject(error);
  }
);

// accessDB用 Axios インスタンス
export const accessDBApi = axios.create({
  baseURL: ACCESSDB_API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

accessDBApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const key = normalizeEndpointKey(config.url);
    const guard = evaluateGuard({ endpointKey: key });
    if (!guard.allowed) {
      return Promise.reject(new axios.Cancel(`Guard blocked: ${guard.reason}`));
    }
    const { isSessionExpired } = useAuthStore.getState();
    const idToken = getIdTokenWithFallback();
    if (isSessionExpired) {
      return Promise.reject(new axios.Cancel('Session expired'));
    }
    if (idToken && config.headers) {
      config.headers.Authorization = `Bearer ${idToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

accessDBApi.interceptors.response.use(
  (response: AxiosResponse) => {
    cbRegistry.recordSuccess(normalizeEndpointKey(response.config?.url));
    return response;
  },
  async (rawError: unknown) => {
    if (axios.isCancel(rawError)) {
      return Promise.reject(rawError);
    }
    const error = rawError as AxiosError<ApiResponse<unknown>>;
    const status = error.response?.status;
    const key = normalizeEndpointKey(error.config?.url);
    const responseData = error.response?.data as any;
    const code = responseData?.error?.code ?? '';
    if (status === 401) {
      authGate.on401or403(code);
      return handle401Error(error, accessDBApi, () => useAuthStore.getState().idToken ?? '');
    }
    if (status === 403 && code !== 'PLAN_001') {
      authGate.on401or403(code);
    }
    if ((status && status >= 500) || (!status && !error.response)) {
      cbRegistry.recordFailure(key);
    }
    const actionToast = getActionToastOptions(error, accessDBApi);
    if (actionToast && !error.config?.skipGlobalErrorToast) {
      showToast.error(actionToast.message, actionToast.options);
    }
    return Promise.reject(error);
  }
);

// エラーハンドリング
export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiResponse<unknown>>;
    if (axiosError.response?.status === 429) {
      const responseData = axiosError.response?.data as any;
      const retryAfter = responseData?.retry_after;
      const message = retryAfter
        ? `操作回数の上限に達しました。${retryAfter}秒後に再試行してください。`
        : 'リクエスト制限に達しました。しばらくお待ちください。';
      return { code: 'RATE_LIMIT_EXCEEDED', message };
    }
    if (axiosError.response?.data?.error && typeof axiosError.response.data.error === 'object') {
      const beError = axiosError.response.data.error as ApiError;
      const isEnglish = /^[\x20-\x7E]+$/.test(beError.message ?? '');
      const message = isEnglish ? getErrorMessageByCode(beError.code) : beError.message;
      return { ...beError, message };
    }
    if (axiosError.response?.status === 404) {
      return { code: 'NOT_FOUND', message: 'リソースが見つかりません' };
    }
    if (axiosError.response?.status === 403) {
      return { code: 'FORBIDDEN', message: '権限がありません' };
    }
    if (axiosError.response?.status === 500) {
      return { code: 'INTERNAL_SERVER_ERROR', message: getErrorMessageByCode('500') };
    }
    if (axiosError.response?.status === 502) {
      return { code: 'BAD_GATEWAY', message: getErrorMessageByCode('502') };
    }
    if (axiosError.response?.status === 503) {
      return { code: 'SERVICE_UNAVAILABLE', message: getErrorMessageByCode('503') };
    }
    if (axiosError.code === 'ECONNABORTED') {
      return { code: 'TIMEOUT', message: 'リクエストがタイムアウトしました' };
    }
    if (axiosError.message === 'Network Error') {
      return { code: 'NETWORK_ERROR', message: 'ネットワークエラーが発生しました' };
    }
  }
  return { code: 'UNKNOWN', message: '予期しないエラーが発生しました' };
};
